import { AxiosError, AxiosRequestConfig } from 'axios';
import { apiGet, apiGetWithCancelation, apiPost, apiPut, apiPutFile } from './api';
import {
  BillingFileData,
  DTOgetOrderDetailedData,
  IproviderInfo,
  DTOcreateNewProvider,
  DTOupdateOrderProductsInformation,
  IordersByType,
  IProductList,
  DTOgetOrderFilters,
  DTOpostPaymentLink,
  IDataShopping,
  IcreateOrderComment,
  IReportData,
  IupdateShipmentInformation,
  IUpdateConfirmEdition,
  IUpdateTaxInfo,
  IupdateOrderPurchaseOrder,
  DTOgetKamFilters,
  ISendProductsToWarehouse,
  IUpdateTemporaryStorage,
  DTOcreateNewReseller,
  IReseller,
  IMPNInfo,
  INPMCreated,
  IBulkTemporaryStorageRequest,
  IBulkTemporaryStorageResponse
} from '../types/orders';
import {
  IInvoiceLogisticServiceRecord,
  IInvoiceOrderRecord,
  IInvoiceRecord
} from 'components/InvoiceSectionOfBillingTab/InvoiceSectionOfBillingTab.types';

const { REACT_APP_SUBSCRIPTION, REACT_APP_CATALOG, REACT_APP_ORGANIZATION } = process.env;

const getSaleOrdersByState = async () => {
  return apiGet<AxiosRequestConfig, IordersByType>(`${REACT_APP_SUBSCRIPTION}/order/backoffice/summary`)
    .then(response => Promise.resolve(response))
    .catch((error: AxiosError) => Promise.reject(error));
};

const uploadInvoice = async (billingFileData: BillingFileData) => {
  return apiPut(`${REACT_APP_SUBSCRIPTION}/order/invoice`, billingFileData)
    .then(response => Promise.resolve(response))
    .catch((error: AxiosError) => Promise.reject(error));
};

const getListOfOrdersFirstView = async (queryParams: string) => {
  return apiGetWithCancelation<AxiosRequestConfig, IProductList[]>(
    `${REACT_APP_SUBSCRIPTION}/order${queryParams || ''}`
  )
    .then(response => Promise.resolve(response))
    .catch((error: AxiosError) => Promise.reject(error));
};

const getListOfOrdersSecondView = async (queryParams?: string) => {
  return apiGet<AxiosRequestConfig, IDataShopping>(`${REACT_APP_SUBSCRIPTION}/order${queryParams || ''}`)
    .then(response => Promise.resolve(response))
    .catch((error: AxiosError) => Promise.reject(error));
};

/**
 * Obtiene las órdenes filtradas por statusId
 * @param statusId - ID del estado de la orden (1: Pending, 2: Active, 3: Delivered, 4: Cancelled)
 * @param queryParams - Parámetros de consulta opcionales (filtros, búsqueda, etc.)
 */
const getOrdersByStatus = async (statusId: number, queryParams?: string) => {
  return apiGet<AxiosRequestConfig, IProductList[]>(
    `${REACT_APP_SUBSCRIPTION}/order/backoffice/trello/${statusId}${queryParams || ''}`
  )
    .then(response => Promise.resolve(response))
    .catch((error: AxiosError) => Promise.reject(error));
};

const updateOrderStatus = async (orderId: number | string, statusId: number, formData: FormData): Promise<string> => {
  return apiPut<AxiosRequestConfig, FormData>(
    `${REACT_APP_SUBSCRIPTION}/order/update-status/${orderId}/${statusId}`,
    formData
  )
    .then(resp => {
      const updateOrder: string = resp;
      return updateOrder;
    })
    .catch((error: AxiosError) => Promise.reject(error));
};

const updateVoucher = async (orderId: number | string, formData: FormData): Promise<string> => {
  return apiPut<AxiosRequestConfig, FormData>(
    `${REACT_APP_SUBSCRIPTION}/order/backoffice/proof-of-payment/${orderId}`,
    formData
  )
    .then(resp => {
      const updateDoc: string = resp;
      return updateDoc;
    })
    .catch((error: AxiosError) => Promise.reject(error));
};

const getOrderDetailedData = async (orderId: number | string) => {
  return apiGet<AxiosRequestConfig, DTOgetOrderDetailedData>(
    `${REACT_APP_SUBSCRIPTION}/order/backoffice/detail/${orderId}`
  )
    .then(response => Promise.resolve(response))
    .catch((error: AxiosError) => Promise.reject(error));
};

const getProviders = async (filter: string) => {
  return apiGet<AxiosRequestConfig, IproviderInfo[]>(`${REACT_APP_CATALOG}catalog/providers?filter=${filter}`)
    .then(response => Promise.resolve(response))
    .catch((error: AxiosError) => Promise.reject(error));
};

const getMPNs = async (brand: string, filter: string) => {
  return apiGet<AxiosRequestConfig, IMPNInfo[]>(
    `${REACT_APP_SUBSCRIPTION}/mpn/search?brand=${brand}&searchTerm=${filter}`
  )
    .then(response => Promise.resolve(response))
    .catch((error: AxiosError) => Promise.reject(error));
};

const createNewMPN = async ({ brand, mpn }: { brand: string; mpn: string }) => {
  return apiPost<AxiosRequestConfig, { brand: string; mpn: string }, { data: INPMCreated }>(
    `${REACT_APP_SUBSCRIPTION}/mpn`,
    { brand, mpn }
  )
    .then(response => Promise.resolve(response))
    .catch((error: AxiosError) => Promise.reject(error));
};

const updateOrderProductsInformation = async (body: DTOupdateOrderProductsInformation[]) => {
  return apiPut<AxiosRequestConfig, DTOupdateOrderProductsInformation[]>(
    `${REACT_APP_SUBSCRIPTION}/order/sale-information`,
    body
  )
    .then(response => Promise.resolve(response))
    .catch((error: AxiosError) => {
      Promise.reject(error);
      throw error;
    });
};

const generateSerialAutomatic = async (productsIds: number[], isVisible: boolean) => {
  return apiPost(`${REACT_APP_SUBSCRIPTION}/product/set-visible-serial`, { productsIds, isVisible })
    .then(response => Promise.resolve(response))
    .catch((error: AxiosError) => Promise.reject(error));
};

const createNewProvider = async (body: DTOcreateNewProvider): Promise<IproviderInfo> => {
  return apiPost(`${REACT_APP_CATALOG}provider`, body)
    .then(response => Promise.resolve(response.data))
    .catch((error: AxiosError) => Promise.reject(error));
};

const getOrderFilters = async () => {
  return apiGet<AxiosRequestConfig, DTOgetOrderFilters>(`${REACT_APP_SUBSCRIPTION}/filters/backoffice/amount-filter`)
    .then(response => Promise.resolve(response))
    .catch((error: AxiosError) => Promise.reject(error));
};

const getKAMUserFilter = async () => {
  return apiGet<AxiosRequestConfig, DTOgetKamFilters[]>(
    `${REACT_APP_ORGANIZATION}/organization/backoffice/kam-user/filter`
  )
    .then(response => Promise.resolve(response))
    .catch((error: AxiosError) => Promise.reject(error));
};

const postPaymentLink = async (orderId: number | string, body: DTOpostPaymentLink) => {
  return apiPut<AxiosRequestConfig, DTOpostPaymentLink>(`${REACT_APP_SUBSCRIPTION}/order/payment-link/${orderId}`, body)
    .then(response => Promise.resolve(response))
    .catch((error: AxiosError) => Promise.reject(error));
};

const updateNewInvoiceNumber = async (orderId: number, invoiceNumber: string) => {
  return apiPut<AxiosRequestConfig>(`${REACT_APP_SUBSCRIPTION}/order/invoice-number/${orderId}/${invoiceNumber}`)
    .then(response => Promise.resolve(response))
    .catch((error: AxiosError) => Promise.reject(error));
};

const updateInvoiceRecord = async (
  invoiceRecordId: number,
  invoiceRecord: IInvoiceOrderRecord
): Promise<IInvoiceRecord> => {
  return apiPut<AxiosRequestConfig, IInvoiceRecord>(
    `${REACT_APP_SUBSCRIPTION}/order/invoice-record/${invoiceRecordId}`,
    invoiceRecord
  )
    .then(response => Promise.resolve(response))
    .catch((error: AxiosError) => Promise.reject(error));
};

const createInvoiceRecord = async (invoiceRecord: IInvoiceOrderRecord) => {
  return apiPost<AxiosRequestConfig, IInvoiceRecord, { data: IInvoiceRecord }>(
    `${REACT_APP_SUBSCRIPTION}/order/invoice-record`,
    invoiceRecord
  )
    .then(response => Promise.resolve(response))
    .catch((error: AxiosError) => Promise.reject(error));
};

const getInvoiceOrderRecord = async (invoiceId: number) => {
  return apiGet<AxiosRequestConfig, IInvoiceOrderRecord[]>(
    `${REACT_APP_SUBSCRIPTION}/invoice-record/by-order/${invoiceId}`
  )
    .then(response => Promise.resolve(response))
    .catch((error: AxiosError) => Promise.reject(error));
};

const getInvoiceLogisticServiceRecord = async (invoiceId: number) => {
  return apiGet<AxiosRequestConfig, IInvoiceLogisticServiceRecord[]>(
    `${REACT_APP_SUBSCRIPTION}/invoice-record/by-logistic-service/${invoiceId}`
  )
    .then(response => Promise.resolve(response))
    .catch((error: AxiosError) => Promise.reject(error));
};

const updateNewCreditDate = async (orderId: number, date: string, sendNotificationEmail: boolean = true) => {
  const queryParam = sendNotificationEmail ? '' : '?sendNotificationEmail=false';
  return apiPut<AxiosRequestConfig>(
    `${REACT_APP_SUBSCRIPTION}/order/committed-payment-date/${orderId}/${date}${queryParam}`
  )
    .then(response => Promise.resolve(response))
    .catch((error: AxiosError) => Promise.reject(error));
};

export const createOrderComment = async (body: IcreateOrderComment) => {
  const formData = new FormData();
  const bodyJson = JSON.stringify(body);
  formData.append('body', bodyJson);
  body?.commentImage && formData.append('file', body.commentImage);
  return apiPost<AxiosRequestConfig, FormData>(`${REACT_APP_SUBSCRIPTION}/order/comment/backoffice`, formData)
    .then(response => Promise.resolve(response))
    .catch((error: AxiosError) => Promise.reject(error));
};

const sendWrongAddressReport = async (body: IReportData) => {
  return apiPost<AxiosRequestConfig, IReportData>(`${REACT_APP_SUBSCRIPTION}/shipment/report/address`, body)
    .then(response => Promise.resolve(response))
    .catch((error: AxiosError) => Promise.reject(error));
};

const updateShipmentInformation = async (
  shipmentGroupId: number | string,
  body: IupdateShipmentInformation,
  organizationId?: number | string,
  userId?: number | string
): Promise<string> => {
  const updatingId = userId ? `?userId=${userId}` : `?organizationId=${organizationId}`;
  return apiPut(`${REACT_APP_SUBSCRIPTION}/shipment/backoffice/shipment-info/${shipmentGroupId}${updatingId}`, body)
    .then(response => Promise.resolve(response))
    .catch((error: AxiosError) => Promise.reject(error));
};

const updateConfirmEdition = async (body: IUpdateConfirmEdition): Promise<string> => {
  return apiPut(`${REACT_APP_SUBSCRIPTION}/shipment/report/status`, body)
    .then(response => Promise.resolve(response))
    .catch((error: AxiosError) => Promise.reject(error));
};

const sendProductsToWarehouse = async (body: ISendProductsToWarehouse): Promise<string> => {
  return apiPut(`${REACT_APP_SUBSCRIPTION}/shipment/change-destination/warehouse`, body)
    .then(response => Promise.resolve(response))
    .catch((error: AxiosError) => Promise.reject(error));
};

const updateAdditionalTaxInfo = async (orderId: number, bodiAdditionalTax: IUpdateTaxInfo) => {
  return apiPut(`${REACT_APP_SUBSCRIPTION}/order/update-tax-info/${orderId}`, bodiAdditionalTax)
    .then(response => Promise.resolve(response))
    .catch((error: AxiosError) => Promise.reject(error));
};

const updateOrderPurchaseOrder = async (body: IupdateOrderPurchaseOrder) => {
  return apiPut<AxiosRequestConfig, IupdateOrderPurchaseOrder>(`${REACT_APP_SUBSCRIPTION}/order/purchase-order`, body);
};

const getQRCode = async (productId: number) => {
  return apiGet<AxiosRequestConfig, string>(`${REACT_APP_SUBSCRIPTION}/product/generate-qr/${productId}`)
    .then(response => Promise.resolve(response))
    .catch((error: AxiosError) => Promise.reject(error));
};

const updateAdditionalConfigurationStatus = async (body: FormData) => {
  return apiPutFile(`${REACT_APP_SUBSCRIPTION}/product-config/set-status`, body)
    .then(response => Promise.resolve(response))
    .catch((error: AxiosError) => Promise.reject(error));
};

const createTemporaryStorageForShipment = async (body: IUpdateTemporaryStorage) => {
  return apiPost(`${REACT_APP_SUBSCRIPTION}/warehouse-temporary-storage/manage/shipment`, body)
    .then(response => Promise.resolve(response))
    .catch((error: AxiosError) => Promise.reject(error));
};

const updateTemporaryStorageForShipment = async (body: IUpdateTemporaryStorage) => {
  return apiPut(`${REACT_APP_SUBSCRIPTION}/warehouse-temporary-storage/manage/shipment/${body.shipmentId}`, body)
    .then(response => Promise.resolve(response))
    .catch((error: AxiosError) => Promise.reject(error));
};

const cancelTemporaryStorageForShipment = async (shipmentId: number) => {
  return apiPost(`${REACT_APP_SUBSCRIPTION}/warehouse-temporary-storage/manage/shipment/${shipmentId}/cancel`)
    .then(response => Promise.resolve(response))
    .catch((error: AxiosError) => Promise.reject(error));
};

const createBulkTemporaryStorageForShipments = async (
  body: IBulkTemporaryStorageRequest
): Promise<IBulkTemporaryStorageResponse[]> => {
  return apiPost<AxiosRequestConfig, IBulkTemporaryStorageRequest>(
    `${REACT_APP_SUBSCRIPTION}/warehouse-temporary-storage/manage/bulk-shipments`,
    body
  )
    .then(response => Promise.resolve(response.data))
    .catch((error: AxiosError) => Promise.reject(error));
};

const getResellersByCountryId = async (countryId: number) => {
  return apiGet<AxiosRequestConfig, IReseller[]>(`${REACT_APP_CATALOG}reseller/by-country/${countryId}`)
    .then(response => Promise.resolve(response))
    .catch((error: AxiosError) => Promise.reject(error));
};

const createNewReseller = async (body: DTOcreateNewReseller): Promise<IReseller> => {
  return apiPost<AxiosRequestConfig, DTOcreateNewReseller>(`${REACT_APP_CATALOG}reseller`, body)
    .then(response => Promise.resolve(response.data))
    .catch((error: AxiosError) => Promise.reject(error));
};

const sendRequireConfirmConfigurationEmail = async (orderId: number): Promise<string> => {
  return apiPut<AxiosRequestConfig, number>(`${REACT_APP_SUBSCRIPTION}/order/configuration-confirmation/${orderId}`)
    .then(response => Promise.resolve(response))
    .catch((error: AxiosError) => Promise.reject(error));
};

const requestOrderSummaryPDF = async (orderId: number): Promise<string> => {
  return apiGet<AxiosRequestConfig, { url: string }>(`${REACT_APP_SUBSCRIPTION}/order/resume/pdf?orderId=${orderId}`)
    .then(response => Promise.resolve((response as { url: string }).url))
    .catch((error: AxiosError) => Promise.reject(error));
};

export {
  getSaleOrdersByState,
  updateOrderStatus,
  getOrderDetailedData,
  getProviders,
  getMPNs,
  createNewMPN,
  updateOrderProductsInformation,
  createNewProvider,
  updateVoucher,
  uploadInvoice,
  getOrderFilters,
  postPaymentLink,
  updateNewCreditDate,
  updateNewInvoiceNumber,
  updateInvoiceRecord,
  createInvoiceRecord,
  getInvoiceOrderRecord,
  getInvoiceLogisticServiceRecord,
  getListOfOrdersFirstView,
  getListOfOrdersSecondView,
  getOrdersByStatus,
  sendWrongAddressReport,
  updateShipmentInformation,
  updateConfirmEdition,
  updateAdditionalTaxInfo,
  updateOrderPurchaseOrder,
  getKAMUserFilter,
  sendProductsToWarehouse,
  getQRCode,
  updateAdditionalConfigurationStatus,
  createTemporaryStorageForShipment,
  updateTemporaryStorageForShipment,
  cancelTemporaryStorageForShipment,
  createBulkTemporaryStorageForShipments,
  generateSerialAutomatic,
  getResellersByCountryId,
  createNewReseller,
  sendRequireConfirmConfigurationEmail,
  requestOrderSummaryPDF
};
